import type { Shipping } from '../types';
import { getShippingCompanyLabel, getStatusInfo } from '../types';

/**
 * Escape CSV value to prevent injection and handle special characters
 */
function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Format date for CSV export
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  } catch {
    return '';
  }
}

const COLUMNS = [
  { key: 'slipNumber', label: 'Slip #', getValue: (s: Shipping) => s.slipNumber },
  { key: 'quoteNumber', label: 'Quote #', getValue: (s: Shipping) => s.quoteNumber || '' },
  { key: 'quoteProjectName', label: 'Project Name', getValue: (s: Shipping) => s.quoteProjectName || '' },
  { key: 'status', label: 'Status', getValue: (s: Shipping) => getStatusInfo(s.status).label },
  { key: 'shippingCompany', label: 'Company', getValue: (s: Shipping) => getShippingCompanyLabel(s.shippingCompany) },
  { key: 'trackingNumber', label: 'Tracking #', getValue: (s: Shipping) => s.trackingNumber || '' },
  { key: 'shippedDate', label: 'Shipped Date', getValue: (s: Shipping) => formatDate(s.shippedDate) },
  { key: 'receivedDate', label: 'Received Date', getValue: (s: Shipping) => formatDate(s.receivedDate) },
  { key: 'shipToSiteName', label: 'Ship To Site', getValue: (s: Shipping) => s.shipToSiteName || '' },
  { key: 'attentionTo', label: 'Attention To', getValue: (s: Shipping) => s.attentionTo || '' },
  { key: 'preparedByName', label: 'Prepared By', getValue: (s: Shipping) => s.preparedByName || '' },
  { key: 'verifiedByName', label: 'Verified By', getValue: (s: Shipping) => s.verifiedByName || '' },
  { key: 'createdAt', label: 'Created At', getValue: (s: Shipping) => formatDate(s.createdAt) },
];

/**
 * Generate CSV content string from shippings data
 */
export function generateCSVContent(shippings: Shipping[]): string {
  const header = COLUMNS.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = shippings.map((shipping) =>
    COLUMNS.map((c) => escapeCSVValue(c.getValue(shipping))).join(',')
  );
  return [header, ...rows].join('\n');
}

/**
 * Export shippings to CSV file
 */
export function exportShippingsToCSV(shippings: Shipping[], filename = 'shippings'): void {
  const csvContent = generateCSVContent(shippings);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy shippings data to clipboard as CSV text
 */
export async function copyShippingsToClipboard(shippings: Shipping[]): Promise<void> {
  const csvContent = generateCSVContent(shippings);
  await navigator.clipboard.writeText(csvContent);
}
